// pages/processamentos-ia/components/ProcessamentoIADetailsDialog.tsx
import { useState } from "react";
import { format, differenceInMilliseconds } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Copy,
  Check,
  TerminalSquare,
  FileCode2,
  Bot,
  CalendarDays,
  UserSquare2,
  AlertTriangle,
  Loader2,
  Sparkles,
  ServerCrash,
  RefreshCw,
  Timer,
  History,
} from "lucide-react";
import { toast } from "sonner";

import { EntidadeReferenciaProcessamentoIAMap, TipoProcessamentoIAMap } from "@workflow/types";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

import { PersonBadge } from "@/components/shared/PersonBadge";
import { ProcessamentoIAStatusBadge } from "@/components/processamentos-ia/ProcessamentoStatusBadge";
import { cn } from "@/lib/utils";
import { useProcessamentoIADetails } from "@/hooks/processamento-ia/use-processamento-ia-details";
import { useReprocessarProcessamentoIA } from "@/hooks/processamento-ia/use-reprocessar-processamento-ia";

// Formata uma data de forma compacta, que cabe nos cartões sem truncar.
const formatDate = (value?: Date | string | null) =>
  value ? format(new Date(value), "dd MMM yyyy '·' HH:mm", { locale: ptBR }) : "—";

// Converte a diferença entre criação e conclusão em algo legível ("1,2 s", "3 min").
const formatDuration = (start?: Date | string | null, end?: Date | string | null) => {
  if (!start || !end) return null;
  const ms = differenceInMilliseconds(new Date(end), new Date(start));
  if (ms < 0) return null;
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return sec ? `${min} min ${sec} s` : `${min} min`;
};

// Cartão de informação simples, usado na faixa de resumo do topo.
const InfoCard = ({
  icon,
  iconClass,
  label,
  children,
  className,
}: {
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-sm dark:border-border dark:bg-card/60",
      className,
    )}
  >
    <div className={cn("shrink-0 rounded-lg p-2", iconClass)}>{icon}</div>
    <div className="flex min-w-0 flex-col justify-center">
      <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="text-sm font-semibold leading-tight text-foreground">{children}</div>
    </div>
  </div>
);

// 🚀 Terminal Premium: Fica perfeito no Light e Dark mode usando tons de Slate
const JsonViewer = ({ data, emptyMessage }: { data: unknown; emptyMessage: string }) => {
  const [copied, setCopied] = useState(false);

  if (!data) {
    return (
      <div className="flex w-full min-w-0 flex-col items-center justify-center rounded-xl border border-dashed bg-slate-50 py-16 text-muted-foreground dark:bg-slate-900/20">
        <TerminalSquare className="mb-4 h-10 w-10 opacity-20" />
        <p className="max-w-[250px] text-center text-sm font-medium">{emptyMessage}</p>
      </div>
    );
  }

  let formattedData = data;
  try {
    if (typeof data === "object") formattedData = JSON.stringify(data, null, 2);
    else if (typeof data === "string") formattedData = JSON.stringify(JSON.parse(data), null, 2);
  } catch (e) {
    // Fallback silencioso
  }

  const handleCopy = () => {
    const textToCopy =
      typeof formattedData === "string" ? formattedData : JSON.stringify(formattedData);
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success("Payload copiado com sucesso!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-950 shadow-md dark:border-slate-800">
      <div className="absolute top-0 z-10 flex w-full items-center justify-between border-b border-slate-800 bg-slate-900/90 px-4 py-2 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-slate-700" />
            <div className="h-2.5 w-2.5 rounded-full bg-slate-700" />
            <div className="h-2.5 w-2.5 rounded-full bg-slate-700" />
          </div>
          <span className="ml-2 font-mono text-xs text-slate-400">payload.json</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 rounded-md px-3 text-slate-300 transition-all hover:bg-slate-800 hover:text-white",
            copied && "text-emerald-400 hover:bg-emerald-400/10 hover:text-emerald-300",
          )}
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Copy className="mr-1.5 h-3.5 w-3.5" />
          )}
          {copied ? "Copiado" : "Copiar"}
        </Button>
      </div>

      <div className="custom-scrollbar max-h-[55vh] w-full min-w-0 overflow-x-auto overflow-y-auto pt-12">
        <pre className="w-full min-w-0 p-5 font-mono text-[13px] leading-relaxed text-slate-300">
          <code>{formattedData as string}</code>
        </pre>
      </div>
    </div>
  );
};

interface ProcessamentoIADetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  processamentoID: number | null;
}

export function ProcessamentoIADetailsDialog({
  open,
  onOpenChange,
  processamentoID,
}: ProcessamentoIADetailsDialogProps) {
  const { processamento, isLoading, error } = useProcessamentoIADetails(processamentoID || 0);
  const reprocessar = useReprocessarProcessamentoIA();

  const siglaEntidade = processamento
    ? EntidadeReferenciaProcessamentoIAMap[processamento.entidadeTipo] || processamento.entidadeTipo
    : "";

  const tipoFormatado = processamento
    ? TipoProcessamentoIAMap[processamento.tipoProcessamento] || processamento.tipoProcessamento
    : "";

  const duracao =
    processamento && processamento.status !== "PENDENTE"
      ? formatDuration(
          processamento.criadoEm,
          processamento.dataConclusao ?? processamento.alteradoEm,
        )
      : null;

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-6xl flex-col overflow-hidden border-border/60 bg-background p-0 shadow-2xl md:h-[85vh]">
        {/* ESTADO 1: CARREGANDO */}
        {isLoading && (
          <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center bg-slate-50/50 p-12 dark:bg-transparent">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
              <Bot className="relative z-10 h-12 w-12 animate-pulse text-primary" />
            </div>
            <h3 className="mt-6 text-lg font-semibold text-foreground">Acessando Banco de Dados</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Carregando telemetria da inteligência artificial...
            </p>
          </div>
        )}

        {/* ESTADO 2: ERRO DA API */}
        {!isLoading && error && (
          <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center bg-slate-50/50 p-12 text-center dark:bg-transparent">
            <div className="mb-6 rounded-full border border-red-200 bg-red-100 p-5 shadow-sm dark:border-red-900/30 dark:bg-red-900/20">
              <ServerCrash className="h-10 w-10 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-foreground">Ops! Falha na conexão.</h3>
            <p className="mb-8 max-w-md text-base text-muted-foreground">
              Não conseguimos recuperar os detalhes do processamento{" "}
              <strong>#{processamentoID}</strong>. O registro pode ter sido removido ou há
              instabilidade na rede.
            </p>
            <Button onClick={() => handleClose(false)} size="lg" className="px-8 shadow-sm">
              Fechar Janela
            </Button>
          </div>
        )}

        {/* ESTADO 3: SUCESSO */}
        {!isLoading && !error && processamento && (
          <>
            <div className="shrink-0 border-b border-border bg-gradient-to-r from-primary/5 to-transparent p-4 pr-14 dark:from-primary/10 md:p-6">
              <DialogHeader>
                <div className="flex min-w-0 items-center gap-4">
                  <div className="shrink-0 rounded-xl border border-primary/20 bg-primary/10 p-2.5 text-primary shadow-sm">
                    <Bot className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="flex flex-wrap items-center gap-3 text-xl font-bold text-foreground md:text-2xl">
                      Processamento{" "}
                      <span className="font-medium text-muted-foreground">#{processamento.id}</span>
                      <ProcessamentoIAStatusBadge status={processamento.status} />
                    </DialogTitle>
                    <DialogDescription className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-semibold tracking-tight text-foreground">
                        {tipoFormatado}
                      </span>
                      <span className="text-muted-foreground/40">•</span>
                      <Badge
                        variant="secondary"
                        className="border-border bg-white px-2 py-0.5 font-mono text-xs shadow-sm dark:bg-background"
                      >
                        {siglaEntidade}-{processamento.entidadeId}
                      </Badge>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
            </div>

            <div className="custom-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-background p-4 md:p-6">
              <div className="flex w-full min-w-0 flex-col">
                {/* FAIXA DE RESUMO: o que importa, sempre visível e legível */}
                <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                  <InfoCard
                    icon={<CalendarDays className="h-4 w-4" />}
                    iconClass="bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-500"
                    label="Criado em"
                  >
                    {formatDate(processamento.criadoEm)}
                  </InfoCard>

                  <InfoCard
                    icon={<Timer className="h-4 w-4" />}
                    iconClass="bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-500"
                    label={processamento.status === "PENDENTE" ? "Status" : "Duração"}
                  >
                    {processamento.status === "PENDENTE" ? (
                      <span className="text-amber-600 dark:text-amber-500">Em andamento…</span>
                    ) : (
                      (duracao ?? "—")
                    )}
                  </InfoCard>

                  <InfoCard
                    icon={<History className="h-4 w-4" />}
                    iconClass="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500"
                    label="Última atualização"
                  >
                    {formatDate(processamento.alteradoEm)}
                  </InfoCard>

                  <InfoCard
                    icon={<UserSquare2 className="h-4 w-4" />}
                    iconClass="bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-500"
                    label="Solicitado por"
                  >
                    {processamento.criadoPor ? (
                      <PersonBadge pessoa={processamento.criadoPor} />
                    ) : (
                      <span className="font-medium text-muted-foreground">
                        Sistema (automático)
                      </span>
                    )}
                  </InfoCard>
                </div>

                {/* ESTADO DE ERRO DA IA (SENTRY-STYLE) */}
                {processamento.status === "FALHA" && processamento.mensagemErro && (
                  <div className="relative mb-6 flex w-full flex-col gap-4 overflow-hidden rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm dark:border-red-900/50 dark:bg-red-950/20 sm:flex-row">
                    <div className="absolute bottom-0 left-0 top-0 w-1.5 bg-red-500" />
                    <div className="h-fit shrink-0 rounded-full bg-red-100 p-2 text-red-600 dark:bg-red-900/40 dark:text-red-400">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div className="w-full min-w-0 overflow-hidden">
                      <h4 className="mb-1.5 text-base font-bold text-red-900 dark:text-red-300">
                        Incidente na Execução
                      </h4>
                      <div className="rounded-lg border border-red-100 bg-white/60 p-3 dark:border-red-900/30 dark:bg-black/20">
                        <p className="whitespace-pre-wrap break-words font-mono text-sm text-red-800 dark:text-red-300/90">
                          {processamento.mensagemErro}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Tabs
                  defaultValue={processamento.status === "FALHA" ? "entrada" : "resultado"}
                  className="flex w-full min-w-0 flex-col"
                >
                  <TabsList className="mb-6 grid h-auto min-h-[52px] w-full shrink-0 grid-cols-2 rounded-xl bg-slate-100 p-1.5 dark:bg-muted/50">
                    <TabsTrigger
                      value="entrada"
                      className="h-full gap-2 whitespace-normal rounded-lg py-2 text-center text-xs transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-background md:text-sm"
                    >
                      <FileCode2 className="hidden h-4 w-4 shrink-0 opacity-70 md:block" />
                      Entrada
                    </TabsTrigger>
                    <TabsTrigger
                      value="resultado"
                      className="h-full gap-2 whitespace-normal rounded-lg py-2 text-center text-xs transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-background md:text-sm"
                    >
                      <Sparkles className="hidden h-4 w-4 shrink-0 text-primary opacity-70 md:block" />
                      Resultado
                    </TabsTrigger>
                  </TabsList>

                  <div className="mt-2 w-full min-w-0 flex-1 outline-none duration-300 animate-in fade-in">
                    <TabsContent value="entrada" className="m-0 w-full min-w-0">
                      <JsonViewer
                        data={processamento.entrada}
                        emptyMessage="A requisição não enviou um payload de entrada."
                      />
                    </TabsContent>

                    <TabsContent value="resultado" className="m-0 w-full min-w-0">
                      {/* ESTADO PENDENTE DINÂMICO PARA A IA */}
                      {processamento.status === "PENDENTE" ? (
                        <div className="flex w-full min-w-0 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-20 text-muted-foreground dark:border-border dark:bg-muted/10">
                          <Loader2 className="mb-5 h-10 w-10 animate-spin text-primary" />
                          <h3 className="mb-2 text-lg font-semibold text-foreground">
                            Análise em Andamento
                          </h3>
                          <p className="max-w-[300px] text-center text-sm leading-relaxed">
                            A inteligência artificial está processando este item. O resultado
                            estruturado aparecerá aqui em breve.
                          </p>
                        </div>
                      ) : (
                        <JsonViewer
                          data={processamento.resultado}
                          emptyMessage="A IA processou o item, mas não retornou dados."
                        />
                      )}
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </div>

            {/* RODAPÉ: ação principal, longe do botão de fechar */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-slate-50 px-4 py-3 dark:bg-muted/10 md:px-6">
              <p className="hidden text-xs text-muted-foreground sm:block">
                Reprocessar executa a análise novamente do zero.
              </p>
              <Button
                className="shrink-0 gap-2"
                disabled={reprocessar.isPending}
                onClick={() => reprocessar.mutate(processamento.id)}
              >
                {reprocessar.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Reprocessar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
